import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { X, FileText, Pencil, Link2, ClipboardList } from "lucide-react";

function EditBriefDialog({ brief, onClose, onSaved }) {
  const [form, setForm] = useState({
    goal: brief.goal || "",
    key_facts: (brief.key_facts || []).join("\n"),
    angle: brief.angle || "",
    cta_suggestion: brief.cta_suggestion || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/newsroom/briefs/${brief.id}`, {
        topic: brief.topic,
        pillar: brief.pillar,
        content_type: brief.content_type,
        goal: form.goal,
        key_facts: form.key_facts.split("\n").map((s) => s.trim()).filter(Boolean),
        angle: form.angle,
        cta_suggestion: form.cta_suggestion,
        internal_links: brief.internal_links || [],
      });
      toast.success("Brief updated");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="edit-brief-dialog">
        <DialogHeader>
          <DialogTitle>Edit Brief — {brief.topic}</DialogTitle>
          <DialogDescription>Adjust the goal, key facts, angle, or CTA before drafting.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-navy mb-1 block">Goal</label>
            <Textarea rows={2} value={form.goal} onChange={(e) => setForm((f) => ({ ...f, goal: e.target.value }))} data-testid="brief-edit-goal" />
          </div>
          <div>
            <label className="text-sm font-medium text-navy mb-1 block">Key Facts (one per line)</label>
            <Textarea rows={4} value={form.key_facts} onChange={(e) => setForm((f) => ({ ...f, key_facts: e.target.value }))} data-testid="brief-edit-key-facts" />
          </div>
          <div>
            <label className="text-sm font-medium text-navy mb-1 block">Angle</label>
            <Textarea rows={2} value={form.angle} onChange={(e) => setForm((f) => ({ ...f, angle: e.target.value }))} data-testid="brief-edit-angle" />
          </div>
          <div>
            <label className="text-sm font-medium text-navy mb-1 block">CTA Suggestion</label>
            <Input value={form.cta_suggestion} onChange={(e) => setForm((f) => ({ ...f, cta_suggestion: e.target.value }))} data-testid="brief-edit-cta" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="brief-edit-cancel-button">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-gold text-navy hover:bg-gold/90" data-testid="brief-edit-save-button">
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminBriefs() {
  const [briefs, setBriefs] = useState(null);
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();

  const load = () => {
    api.get("/admin/newsroom/briefs?status=new").then((res) => setBriefs(res.data)).catch(() => {
      setBriefs([]);
      toast.error("Failed to load briefs");
    });
  };
  useEffect(() => { load(); }, []);

  const dismiss = async (id) => {
    try {
      await api.post(`/admin/newsroom/briefs/${id}/dismiss`);
      setBriefs((prev) => prev.filter((b) => b.id !== id));
      toast.success("Brief dismissed");
    } catch {
      toast.error("Failed to dismiss brief");
    }
  };

  const draftArticle = (brief) => {
    navigate("/admin/articles/new", {
      state: {
        prefillTopic: brief.topic,
        prefillContentType: brief.content_type,
        prefillPillar: brief.pillar,
        prefillBriefId: brief.id,
        prefillMarket: brief.market,
      },
    });
  };

  return (
    <div data-testid="admin-briefs-page">
      <div className="mb-6">
        <h1 className="text-2xl font-serif-heading font-bold text-navy">Editorial Briefs</h1>
        <p className="text-sm text-slate-500 mt-1">Review the goal, key facts, angle, and CTA before drafting — generated from Topic Ideas, Competitor Gaps, and the Newsroom queue.</p>
      </div>

      <div className="space-y-4">
        {briefs?.map((b) => (
          <div key={b.id} className="bg-white rounded-lg border border-slate-200 p-5" data-testid={`brief-item-${b.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="font-semibold text-navy capitalize" data-testid={`brief-topic-${b.id}`}>{b.topic}</h2>
                  {b.pillar && <Badge variant="secondary">{b.pillar}</Badge>}
                  <Badge variant="outline" className="text-[11px]">{b.content_type?.replace("_", " ")}</Badge>
                  <Badge variant="outline" className="text-[11px] capitalize">from {b.source_type?.replace("_", " ")}</Badge>
                  <Badge className="text-[11px] bg-navy text-white" data-testid={`brief-market-${b.id}`}>{b.market || "US"}</Badge>
                </div>
                <p className="text-sm text-slate-600"><span className="font-medium text-navy">Goal:</span> {b.goal}</p>
                <p className="text-sm text-slate-600 mt-1"><span className="font-medium text-navy">Angle:</span> {b.angle}</p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Button size="sm" variant="outline" onClick={() => setEditing(b)} data-testid={`brief-edit-button-${b.id}`}>
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="outline" onClick={() => draftArticle(b)} data-testid={`brief-draft-button-${b.id}`}>
                  <FileText className="w-3.5 h-3.5 mr-1" /> Draft Article
                </Button>
                <Button size="sm" variant="ghost" onClick={() => dismiss(b.id)} data-testid={`brief-dismiss-button-${b.id}`}>
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-100">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase mb-1 flex items-center gap-1"><ClipboardList className="w-3.5 h-3.5" /> Key Facts</p>
                <ul className="text-sm text-slate-600 list-disc pl-4 space-y-0.5" data-testid={`brief-key-facts-${b.id}`}>
                  {(b.key_facts || []).map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase mb-1">CTA Suggestion</p>
                <p className="text-sm text-slate-600 mb-3" data-testid={`brief-cta-${b.id}`}>{b.cta_suggestion || "—"}</p>
                <p className="text-xs font-semibold text-slate-500 uppercase mb-1 flex items-center gap-1"><Link2 className="w-3.5 h-3.5" /> Internal Links</p>
                {(b.internal_links || []).length === 0 ? (
                  <p className="text-sm text-slate-400" data-testid={`brief-internal-links-${b.id}`}>No strong internal-link match found</p>
                ) : (
                  <ul className="text-sm space-y-0.5" data-testid={`brief-internal-links-${b.id}`}>
                    {b.internal_links.map((l, i) => (
                      <li key={i}><a href={l.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{l.title}</a></li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        ))}
        {briefs?.length === 0 && (
          <div className="bg-white rounded-lg border border-slate-200 text-center text-slate-400 py-12">
            No briefs yet. Generate one from Topic Ideas, Competitor Gaps, or the Newsroom queue.
          </div>
        )}
      </div>

      {editing && (
        <EditBriefDialog
          brief={editing}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setBriefs((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}
